// Blog storage utilities using localStorage

const BLOG_STORAGE_KEY = 'daverose_blogs';

/**
 * Blog data structure:
 * {
 *   id: string (unique identifier)
 *   title: string
 *   content: string (HTML or markdown)
 *   excerpt: string (short description)
 *   images: array of {
 *     url: string (Cloudinary URL)
 *     caption: string
 *     publicId: string (Cloudinary public ID)
 *   }
 *   categories: array of strings
 *   tags: array of strings
 *   createdAt: timestamp
 *   updatedAt: timestamp
 *   published: boolean
 * }
 */

// Get all blogs
export const getAllBlogs = () => {
  try {
    const blogs = localStorage.getItem(BLOG_STORAGE_KEY);
    return blogs ? JSON.parse(blogs) : [];
  } catch (error) {
    console.error('Error reading blogs:', error);
    return [];
  }
};

// Get published blogs only
export const getPublishedBlogs = () => {
  return getAllBlogs().filter(blog => blog.published);
};

// Get a single blog by ID
export const getBlogById = (id) => {
  const blogs = getAllBlogs();
  return blogs.find(blog => blog.id === id);
};

// Save a new blog
export const saveBlog = (blogData) => {
  const blogs = getAllBlogs();
  const newBlog = {
    id: Date.now().toString(),
    ...blogData,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  blogs.push(newBlog);
  localStorage.setItem(BLOG_STORAGE_KEY, JSON.stringify(blogs));
  return newBlog;
};

// Update an existing blog
export const updateBlog = (id, blogData) => {
  const blogs = getAllBlogs();
  const index = blogs.findIndex(blog => blog.id === id);

  if (index === -1) {
    throw new Error('Blog not found');
  }

  blogs[index] = {
    ...blogs[index],
    ...blogData,
    updatedAt: Date.now(),
  };

  localStorage.setItem(BLOG_STORAGE_KEY, JSON.stringify(blogs));
  return blogs[index];
};

// Delete a blog
export const deleteBlog = (id) => {
  const blogs = getAllBlogs();
  const filteredBlogs = blogs.filter(blog => blog.id !== id);
  localStorage.setItem(BLOG_STORAGE_KEY, JSON.stringify(filteredBlogs));
  return true;
};

// Get all unique categories
export const getAllCategories = () => {
  const blogs = getAllBlogs();
  const categories = new Set();
  blogs.forEach(blog => {
    if (blog.categories) {
      blog.categories.forEach(cat => categories.add(cat));
    }
  });
  return Array.from(categories);
};

// Get all unique tags
export const getAllTags = () => {
  const blogs = getAllBlogs();
  const tags = new Set();
  blogs.forEach(blog => {
    if (blog.tags) {
      blog.tags.forEach(tag => tags.add(tag));
    }
  });
  return Array.from(tags);
};

// Filter blogs by category
export const getBlogsByCategory = (category) => {
  const blogs = getPublishedBlogs();
  return blogs.filter(blog =>
    blog.categories && blog.categories.includes(category)
  );
};

// Filter blogs by tag
export const getBlogsByTag = (tag) => {
  const blogs = getPublishedBlogs();
  return blogs.filter(blog =>
    blog.tags && blog.tags.includes(tag)
  );
};

// Search blogs
export const searchBlogs = (query) => {
  const blogs = getPublishedBlogs();
  const lowerQuery = query.toLowerCase();
  return blogs.filter(blog =>
    blog.title.toLowerCase().includes(lowerQuery) ||
    blog.content.toLowerCase().includes(lowerQuery) ||
    blog.excerpt.toLowerCase().includes(lowerQuery)
  );
};
